import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../services/api';

function isDiscreetMode(mode) {
  return String(mode || 'PUBLIC').toUpperCase() === 'DISCREET';
}

function ModeBadge({ mode }) {
  if (isDiscreetMode(mode)) {
    return (
      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
        Private (DISCREET)
      </span>
    );
  }
  return (
    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
      Public (PUBLIC)
    </span>
  );
}

function BadgeVisibility({ row }) {
  if (!row.badgeVisibleToOthers) {
    return (
      <span className="text-xs text-gray-500">Badge hidden</span>
    );
  }
  return (
    <span className="text-xs text-green-700 font-medium">Badge visible</span>
  );
}

function VerifiedUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [modeFilter, setModeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [reverifyUser, setReverifyUser] = useState(null);
  const [reverifyReason, setReverifyReason] = useState(
    'An administrator requested a new verification submission.'
  );

  const fetchVerified = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page, limit: 25, state: 'verified' };
      if (modeFilter) params.mode = modeFilter;
      if (search.trim()) params.search = search.trim();

      const response = await adminAPI.getPendingVerifications(params);
      setUsers(response.data.verifications || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotal(response.data.pagination?.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load verified users');
    } finally {
      setLoading(false);
    }
  }, [page, modeFilter, search]);

  useEffect(() => {
    fetchVerified();
  }, [fetchVerified]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleRequireReverification = async () => {
    if (!reverifyUser) return;
    if (!reverifyReason.trim()) {
      alert('Please enter a reason for the user.');
      return;
    }
    if (
      !window.confirm(
        `Require ${reverifyUser.profileName || reverifyUser.email} to verify again? Their verified status will be cleared until they submit a new verification.`
      )
    ) {
      return;
    }

    try {
      setProcessingId(reverifyUser.userId);
      await adminAPI.requireReverification(reverifyUser.userId, {
        retryReason: reverifyReason.trim(),
      });
      alert('User must re-verify. They will be prompted in the app.');
      setReverifyUser(null);
      setReverifyReason(
        'An administrator requested a new verification submission.'
      );
      fetchVerified();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to require re-verification');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Verified Users</h1>
          <p className="text-gray-600 mt-2">
            All profile-verified users by type. Use re-verify to require a new submission.
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/verifications"
            className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold hover:border-pink-500 transition-colors"
          >
            Pending queue
          </Link>
          <button
            type="button"
            onClick={fetchVerified}
            className="px-4 py-2 bg-pink-500 text-white rounded-xl font-semibold hover:bg-pink-600 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      <form
        onSubmit={handleSearchSubmit}
        className="flex flex-wrap gap-3 mb-6 items-end"
      >
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Search
          </label>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Name, email, or user ID"
            className="px-3 py-2 border-2 border-gray-200 rounded-lg w-64"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Type
          </label>
          <select
            value={modeFilter}
            onChange={(e) => {
              setModeFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 border-2 border-gray-200 rounded-lg"
          >
            <option value="">All types</option>
            <option value="PUBLIC">Public only</option>
            <option value="DISCREET">Private only</option>
          </select>
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-gray-800 text-white rounded-lg font-semibold"
        >
          Search
        </button>
        {(search || searchInput) && (
          <button
            type="button"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setPage(1);
            }}
            className="px-4 py-2 text-gray-600 underline"
          >
            Clear
          </button>
        )}
      </form>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <p className="text-sm text-gray-600 mb-4">
        {total} verified user{total === 1 ? '' : 's'}
      </p>

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : users.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <p className="text-gray-600 text-lg">No verified users found</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-lg overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Badge
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Photos
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Verified at
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((row) => (
                  <tr key={row.userId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-900">
                      {row.profileName || '—'}
                      {row.fraudFlagged && (
                        <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">
                          Fraud flag
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">{row.email}</td>
                    <td className="px-4 py-3">
                      <ModeBadge mode={row.verificationMode} />
                    </td>
                    <td className="px-4 py-3">
                      <BadgeVisibility row={row} />
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">
                      {row.profilePictureCount ?? 0}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm whitespace-nowrap">
                      {formatDate(row.verifiedAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Link
                        to={`/users/${row.userId}`}
                        className="px-2 py-1 mr-2 bg-gray-100 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-200"
                      >
                        Profile
                      </Link>
                      <button
                        type="button"
                        disabled={processingId === row.userId}
                        onClick={() => setReverifyUser(row)}
                        className="px-2 py-1 bg-amber-100 text-amber-900 rounded-lg text-xs font-semibold hover:bg-amber-200 disabled:opacity-50"
                      >
                        Re-verify
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-center items-center gap-4 mt-6">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-gray-700 font-semibold">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}

      {reverifyUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Require re-verification
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              <strong>{reverifyUser.profileName || reverifyUser.email}</strong>{' '}
              ({isDiscreetMode(reverifyUser.verificationMode) ? 'Private' : 'Public'}{' '}
              verification) will need to submit a new selfie and video in the app.
            </p>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Reason (shown to user)
            </label>
            <textarea
              value={reverifyReason}
              onChange={(e) => setReverifyReason(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg mb-4"
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setReverifyUser(null)}
                className="px-4 py-2 border-2 border-gray-200 rounded-xl font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRequireReverification}
                disabled={processingId === reverifyUser.userId}
                className="px-4 py-2 bg-amber-600 text-white rounded-xl font-semibold hover:bg-amber-700 disabled:opacity-50"
              >
                {processingId === reverifyUser.userId ? 'Sending…' : 'Require re-verify'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VerifiedUsers;
